import React, { useEffect, useMemo, useState } from 'react'
import { isNil } from 'lodash'
import {
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Radio,
  Select as AntSelect,
  Switch,
} from 'antd'
import {
  useUpdateBillingProfileMutation,
  useWaitForBillingProfilesRefresh,
} from '../../api/client-billing-profile-api-client'
import {
  BillingProfileRecord,
  StripeCustomerDto,
  UpdateStripeCustomerRequestDto,
} from '../../api/dto/client-billing-profile.dto'
import { validateEmailList } from '../helpers'
import { MAX_DUE_DAY, MIN_DUE_DAY, PaymentType } from './types'
import {
  EmailTagsField,
  FormLabel,
  ModalForm,
  ModalLayout,
  RadioOptionRow,
  SpacedFormItemWrapper,
  StripeAccountCard,
  StripeAccountEmpty,
  StripeAccountFields,
  StripeAccountHeader,
  StripeAccountList,
  StripeActiveToggle,
  StripeBankLast4,
  StripeCustomerId,
} from './ClientBillingProfileManagement.styles'
import { ChangeHistoryPanel } from './ChangeHistoryPanel'
import { RecipientRemovalNotice } from './RecipientRemovalNotice'
import { droppedRecipients } from './recipientRemoval'
import { useRecipientRemoval } from './useRecipientRemoval'

interface FieldErrorNode {
  field: string
  errors: unknown[]
}

interface ApiErrorResponse {
  message?: unknown
  error?: string
}

/** Inputs addressed by a single name. Stripe customers are not among them. */
const FIELD_MAP: Record<string, string> = {
  clientName: 'clientName',
  toEmails: 'toEmails',
  paymentType: 'paymentType',
  dueDay: 'dueDay',
}

const STRIPE_INPUTS = new Set(['description', 'isActive'])
const INDEX_PATTERN = /^\d+$/

const isFieldErrorNode = (value: unknown): value is FieldErrorNode => {
  if (typeof value !== 'object' || value === null) return false
  const node = value as FieldErrorNode
  return typeof node.field === 'string' && Array.isArray(node.errors)
}

type FormName = string | (string | number)[]

/**
 * Validation nests a customer under its index in the request
 * (`stripeCustomers` → `0` → `description`). The inputs are keyed by
 * customer id, so an index that resolves lands on that input and a name
 * that matches nothing is handed back to be said out loud.
 */
const placeApiFieldErrors = (
  nodes: unknown[],
  stripeChanges: UpdateStripeCustomerRequestDto[]
): { fields: { name: FormName; errors: string[] }[]; messages: string[] } => {
  const grouped = new Map<string, { name: FormName; errors: string[] }>()
  const messages: string[] = []

  const addField = (name: FormName, errors: string[]) => {
    const key = Array.isArray(name) ? name.join('\0') : name
    const existing = grouped.get(key)
    if (existing) {
      existing.errors.push(...errors)
      return
    }
    grouped.set(key, { name, errors: [...errors] })
  }

  const visit = (node: FieldErrorNode, path: string[]) => {
    const nextPath = [...path, node.field]
    const texts: string[] = []
    for (const error of node.errors) {
      if (typeof error === 'string') texts.push(error)
      else if (isFieldErrorNode(error)) visit(error, nextPath)
    }
    if (texts.length === 0) return

    const stripeName = stripeInputName(nextPath, stripeChanges)
    if (stripeName) {
      addField(stripeName, texts)
      return
    }
    const root = FIELD_MAP[nextPath[0]]
    if (root) {
      addField(root, texts)
      return
    }
    messages.push(...texts)
  }

  for (const node of nodes) {
    if (typeof node === 'string') messages.push(node)
    else if (isFieldErrorNode(node)) visit(node, [])
  }

  return { fields: Array.from(grouped.values()), messages }
}

/** `stripeCustomers.0.description` → the input for that submitted customer. */
const stripeInputName = (
  path: string[],
  stripeChanges: UpdateStripeCustomerRequestDto[]
): (string | number)[] | null => {
  if (path[0] !== 'stripeCustomers' || path.length !== 3) return null
  const [, indexText, input] = path
  if (!STRIPE_INPUTS.has(input) || !INDEX_PATTERN.test(indexText)) return null
  const customerId = stripeChanges[Number(indexText)]?.stripeCustomerId
  if (!customerId) return null
  return ['stripeCustomers', customerId, input]
}

/** Names the customer's first payment method by bank and last four, when it is a bank account. */
const bankAccountLabel = (customer: StripeCustomerDto): string | null => {
  const bankAccount = customer.stripePaymentMethods?.[0]?.us_bank_account
  const last4 = bankAccount?.last4
  if (isNil(last4)) return null
  return `${bankAccount?.bank_name ?? 'Bank'} ••••${last4}`
}

interface StripeCustomerEdit {
  description?: string
  isActive?: boolean
}

/**
 * Only the fields the operator changed, so an untouched customer is not
 * rewritten. A cleared field clears the description rather than storing ''.
 */
const changedStripeCustomers = (
  customers: StripeCustomerDto[],
  edits: Record<string, StripeCustomerEdit | undefined> | undefined
): UpdateStripeCustomerRequestDto[] =>
  customers.flatMap(({ stripeCustomerId, description, isActive }) => {
    const edit = edits?.[stripeCustomerId]
    const change: UpdateStripeCustomerRequestDto = { stripeCustomerId }
    const nextDescription = edit?.description?.trim() || null
    if (nextDescription !== (description ?? null)) {
      change.description = nextDescription
    }
    const nextIsActive = edit?.isActive ?? isActive
    if (nextIsActive !== isActive) {
      change.isActive = nextIsActive
    }
    return Object.keys(change).length > 1 ? [change] : []
  })

interface SubmitValues {
  toEmails: string[]
  paymentType?: string
  dueDay?: number
  stripeCustomers?: Record<string, StripeCustomerEdit | undefined>
}

interface BillingProfileModalProps {
  open: boolean
  record: BillingProfileRecord | null
  onClose: (savedId?: number) => void
}

/**
 * Edits an existing profile. Creating one is its own flow, so the company here
 * is fixed and shown for reference rather than picked.
 */
const BillingProfileModal = ({
  open,
  record,
  onClose,
}: BillingProfileModalProps) => {
  const [form] = Form.useForm()
  const editedRecipients: string[] | undefined = Form.useWatch('toEmails', form)

  // The addresses this edit has dropped, which is what the notice describes.
  // The field is unset until the effect below fills it, and reading that as
  // every recipient dropped would ask the server about all of them on open.
  const removedEmails = useMemo(
    () =>
      isNil(editedRecipients)
        ? []
        : droppedRecipients(
            record?.toEmails ?? [],
            editedRecipients,
            record?.ccEmails ?? []
          ),
    [record, editedRecipients]
  )
  const removal = useRecipientRemoval({
    open,
    clientPublicKey: record?.clientPublicKey ?? null,
    companyName: record?.clientName ?? 'this company',
    profileId: record?.id ?? null,
    removedEmails,
    doneLabel: 'Profile saved',
  })

  useEffect(() => {
    if (!open || isNil(record)) return
    form.setFieldsValue({
      clientName: record.clientName,
      toEmails: record.toEmails ?? [],
      paymentType: record.paymentType ?? undefined,
      dueDay: record.dueDay ?? undefined,
      stripeCustomers: Object.fromEntries(
        (record.stripeCustomers ?? []).map((customer) => [
          customer.stripeCustomerId,
          {
            description: customer.description ?? '',
            isActive: customer.isActive,
          },
        ])
      ),
    })
  }, [open, record, form])

  const stripeCustomers = record?.stripeCustomers ?? []

  const applyApiErrors = (
    error: unknown,
    stripeChanges: UpdateStripeCustomerRequestDto[]
  ) => {
    const axiosError = error as {
      response?: { status?: number; data?: ApiErrorResponse }
    }
    const data = axiosError?.response?.data
    const payload = data?.message

    if (Array.isArray(payload) && payload.length > 0) {
      const { fields, messages } = placeApiFieldErrors(payload, stripeChanges)
      if (fields.length > 0) form.setFields(fields)
      // A customer the inputs cannot show would otherwise end the save
      // with the spinner stopped and nothing on screen.
      if (messages.length > 0) {
        notification.error({
          message: 'Request failed',
          description: messages.join(' · '),
        })
      }
      if (fields.length > 0 || messages.length > 0) return
    }

    const message =
      typeof payload === 'string'
        ? payload
        : (data?.error ?? 'An unexpected error occurred')

    notification.error({
      message: 'Request failed',
      description: message,
    })
  }

  const updateMutation = useUpdateBillingProfileMutation()
  const waitForProfilesRefresh = useWaitForBillingProfilesRefresh()
  // One flag for the whole save. The PATCH, list reload and access revoke each
  // have their own pending state, and switching between them let the button
  // drop its spinner for a frame in between.
  const [isSaving, setIsSaving] = useState(false)

  const handleSubmit = async () => {
    if (isNil(record)) return

    let values: SubmitValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }

    const stripeChanges = changedStripeCustomers(
      stripeCustomers,
      values.stripeCustomers
    )

    setIsSaving(true)
    try {
      await save(record, values, stripeChanges)
    } finally {
      setIsSaving(false)
    }
  }

  const save = async (
    record: BillingProfileRecord,
    values: SubmitValues,
    stripeChanges: UpdateStripeCustomerRequestDto[]
  ) => {
    let updated
    try {
      updated = await updateMutation.mutateAsync({
        id: record.id,
        body: {
          clientName: record.clientName,
          clientPublicKey: record.clientPublicKey ?? undefined,
          toEmails: values.toEmails ?? [],
          paymentType: values.paymentType as PaymentType | undefined,
          dueDay: values.dueDay ?? undefined,
          stripeCustomers: stripeChanges.length > 0 ? stripeChanges : undefined,
        },
      })
    } catch (error) {
      applyApiErrors(error, stripeChanges)
      return
    }

    // Keep the button spinning until the table behind shows the saved values.
    await waitForProfilesRefresh()

    notification.success({ message: 'Billing profile updated successfully' })
    // Only once the profile has stopped listing them: access taken first would
    // be lost for nothing if the save then failed.
    await removal.revoke()
    onClose(updated.id)
  }

  const handleCancel = () => onClose()

  return (
    <Modal
      open={open}
      title="Update profile"
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText="Save changes"
      okButtonProps={{ disabled: removal.isChecking }}
      confirmLoading={isSaving}
      width={480}
      style={{ maxWidth: 'calc(100vw - 32px)' }}
      destroyOnClose
    >
      <ModalLayout>
        <ModalForm>
          <Form form={form} layout="vertical" requiredMark={false}>
            <Form.Item
              name="clientName"
              label={<FormLabel>Company name</FormLabel>}
            >
              {/* Fixed: the profile belongs to this company for its lifetime. */}
              <AntSelect
                disabled
                options={
                  isNil(record)
                    ? []
                    : [{ value: record.clientName, label: record.clientName }]
                }
              />
            </Form.Item>

            <EmailTagsField>
              <Form.Item
                name="toEmails"
                label={<FormLabel>Invoice recipients</FormLabel>}
                rules={[
                  {
                    required: true,
                    message: 'At least one email is required',
                  },
                  { validator: validateEmailList },
                ]}
              >
                <AntSelect
                  mode="tags"
                  tokenSeparators={[',', ' ', ';']}
                  placeholder="billing@company.com"
                  open={false}
                />
              </Form.Item>
            </EmailTagsField>

            <RecipientRemovalNotice
              lines={removal.lines}
              canRevoke={removal.canRevoke}
            />

            <SpacedFormItemWrapper>
              <Form.Item
                name="paymentType"
                label={<FormLabel>Payment type</FormLabel>}
                rules={[
                  { required: true, message: 'Please select a payment type' },
                ]}
              >
                <Radio.Group>
                  <RadioOptionRow>
                    <Radio value="WIRE">Wire</Radio>
                    <Radio value="STRIPE">Stripe</Radio>
                  </RadioOptionRow>
                </Radio.Group>
              </Form.Item>
            </SpacedFormItemWrapper>

            <SpacedFormItemWrapper>
              <FormLabel>Stripe accounts</FormLabel>
              {stripeCustomers.length === 0 ? (
                <StripeAccountEmpty>
                  No Stripe accounts on file
                </StripeAccountEmpty>
              ) : (
                <StripeAccountList>
                  {stripeCustomers.map((customer) => {
                    const bankLabel = bankAccountLabel(customer)
                    return (
                      <StripeAccountCard key={customer.stripeCustomerId}>
                        <StripeAccountHeader>
                          <StripeCustomerId>
                            {customer.stripeCustomerId}
                          </StripeCustomerId>
                          <StripeBankLast4>
                            {bankLabel ?? 'No bank account'}
                          </StripeBankLast4>
                        </StripeAccountHeader>
                        <StripeAccountFields>
                          <Form.Item
                            name={[
                              'stripeCustomers',
                              customer.stripeCustomerId,
                              'description',
                            ]}
                          >
                            <Input
                              placeholder="Description"
                              aria-label={`Description for ${customer.stripeCustomerId}`}
                            />
                          </Form.Item>
                          <StripeActiveToggle>
                            <Form.Item
                              name={[
                                'stripeCustomers',
                                customer.stripeCustomerId,
                                'isActive',
                              ]}
                              valuePropName="checked"
                            >
                              <Switch
                                size="small"
                                aria-label={`Active for ${customer.stripeCustomerId}`}
                              />
                            </Form.Item>
                            Active
                          </StripeActiveToggle>
                        </StripeAccountFields>
                      </StripeAccountCard>
                    )
                  })}
                </StripeAccountList>
              )}
            </SpacedFormItemWrapper>

            <SpacedFormItemWrapper>
              {/* The input is deliberately unbounded: min/max on an
                  InputNumber silently discard a typed 45 rather than telling
                  the operator the range, so the rule below reports it. */}
              <Form.Item
                name="dueDay"
                label={<FormLabel>Due Day</FormLabel>}
                tooltip="Day of the month invoices are due, in the month before the billing period they fund. A due day of 15 makes a June invoice due May 15."
                rules={[
                  {
                    type: 'integer',
                    min: MIN_DUE_DAY,
                    max: MAX_DUE_DAY,
                    message: `Enter a day of the month from ${MIN_DUE_DAY} to ${MAX_DUE_DAY}`,
                  },
                ]}
              >
                <InputNumber
                  precision={0}
                  placeholder="e.g. 15"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </SpacedFormItemWrapper>
          </Form>
        </ModalForm>

        {isNil(record) ? null : (
          <ChangeHistoryPanel record={record} open={open} />
        )}
      </ModalLayout>
    </Modal>
  )
}

export { BillingProfileModal }
